import React from 'react';

import { GridFilterItem, GridFilterOperator } from '@mui/x-data-grid';

import { JSONSchemaType } from 'utils/api/modules/incidents/incidents.types';
import { PickerProps } from 'components/Grid/operators/types';

import { createFilterRegex } from 'utils/regex';
import {
  AmberAlertStatus,
  AmberAlertStatusName,
  RedAlertStatus,
  EntitiesStatus,
  AuditStatus,
  AuditStatusNames,
} from 'core/constants';

import { TaskStatus, TaskStatusNames } from 'utils/api/modules/tasks/tasks.constants';
import { AcknowledgementRequestStatus, AcknowledgementState } from 'utils/api/modules/entities/entities.types';
import AlarmStatusSelect from 'components/GridFilterStatusSelect/AlarmStatusSelect';
import TaskStatusSelect from 'components/GridFilterStatusSelect/TaskStatusSelect';
import AcknowledgementStatusSelect from 'components/GridFilterStatusSelect/AcknowledgementStatusSelect';
import AcknowledgementStateSelect from 'components/GridFilterStatusSelect/AcknowledgementStateSelect';
import { ACK_REQUEST_STATUS_LABELS } from 'components/GridFilterStatusSelect/AcknowledgementStatusSelect/types';
import { ACK_STATE_LABELS } from 'components/GridFilterStatusSelect/AcknowledgementStateSelect/types';
import AmberAlarmStatusSelect from 'components/GridFilterStatusSelect/AmberAlarmStatusSelect';
import EntitiesStatusSelect from 'components/GridFilterStatusSelect/EntitiesStatusSelect';
import InspectionStatusSelect from 'components/GridFilterStatusSelect/InspectionStatusSelect';
import AuditStatusSelect from 'components/GridFilterStatusSelect/AuditStatusSelect';
import ScheduleStatusSelect from 'components/GridFilterStatusSelect/ScheduleStatusSelect';
import { ScheduleListStatus } from 'components/GridFilterStatusSelect/ScheduleStatusSelect/types';
import GroupStatusSelect from 'components/GridFilterStatusSelect/GroupStatusSelect';
import { GroupStatusValue } from 'components/GridFilterStatusSelect/GroupStatusSelect/types';
import GroupTypeSelect from 'components/GridFilterStatusSelect/GroupTypeSelect';
import { MembershipMode } from 'utils/api/modules/memberGroups';

const withoutNegation =
  (PickerComponent: React.FunctionComponent<PickerProps>): React.FunctionComponent<PickerProps> =>
  ({ item, ...props }) => {
    const value = item.value as string | null | undefined;
    return <PickerComponent {...props} item={{ ...item, value: value?.startsWith('-') ? value.slice(1) : value }} />;
  };

export const BaseStatusOperator = (
  PickerComponent: React.FunctionComponent<PickerProps>,
  props: { schema?: Partial<JSONSchemaType> } | undefined,
  customGetValueAsString?: (value: string) => string,
  disableIsNot: boolean = false,
  IsNotPickerComponent?: React.FunctionComponent<PickerProps>
): GridFilterOperator[] =>
  [
    {
      label: 'is',
      value: 'is',
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!filterItem.value) {
          return null;
        }
        const filterRegex = createFilterRegex(filterItem.value);
        return ({ value }: { value: string }): boolean => (value != null ? filterRegex.test(value.toString()) : false);
      },
      InputComponent: PickerComponent,
      InputComponentProps: {
        schema: props?.schema as JSONSchemaType,
        value: null,
      },
      getValueAsString: (value: string) => (customGetValueAsString ? customGetValueAsString(value) : value),
    },
    ...(disableIsNot
      ? []
      : [
          {
            label: 'is not',
            value: 'is not',
            getApplyFilterFn: (filterItem: GridFilterItem) => {
              if (!filterItem.value) {
                return null;
              }
              const filterRegex = createFilterRegex(filterItem.value);
              return ({ value }: { value: string }): boolean =>
                value != null ? filterRegex.test(value.toString()) : false;
            },
            InputComponent: IsNotPickerComponent ? withoutNegation(IsNotPickerComponent) : PickerComponent,
            InputComponentProps: {
              schema: props?.schema as JSONSchemaType,
              value: null,
            },
            getValueAsString: (value: string) =>
              customGetValueAsString ? customGetValueAsString(value.slice(1)) : value.slice(1),
          },
        ]),
  ] as unknown as GridFilterOperator[];

export const AlarmStatusPicker: React.FunctionComponent<PickerProps> = ({ applyValue, item, ...props }) => (
  <AlarmStatusSelect
    {...props}
    onChange={(newValue) => applyValue({ ...item, value: newValue })}
    value={
      item.value && (item.value as string).startsWith('-')
        ? ((item.value as string).slice(1) as RedAlertStatus)
        : (item.value as RedAlertStatus)
    }
  />
);

export const TaskStatusPicker: React.FunctionComponent<PickerProps> = ({ applyValue, item, ...props }) => (
  <TaskStatusSelect
    {...props}
    onChange={(newValue) => applyValue({ ...item, value: newValue })}
    value={
      item.value && (item.value as string).startsWith('-')
        ? ((item.value as string).slice(1) as TaskStatus)
        : (item.value as TaskStatus)
    }
  />
);

export const AmberAlarmStatusPicker: React.FunctionComponent<PickerProps> = ({ applyValue, item, ...props }) => (
  <AmberAlarmStatusSelect
    {...props}
    onChange={(newValue) => applyValue({ ...item, value: newValue })}
    value={
      item.value && (item.value as string).startsWith('-')
        ? ((item.value as string).slice(1) as AmberAlertStatus)
        : (item.value as AmberAlertStatus)
    }
  />
);

export const EntitiesStatusPicker: React.FunctionComponent<PickerProps> = ({ applyValue, item, ...props }) => (
  <EntitiesStatusSelect
    {...props}
    onChange={(newValue) => applyValue({ ...item, value: newValue })}
    value={
      item.value && (item.value as string).startsWith('-')
        ? ((item.value as string).slice(1) as EntitiesStatus)
        : (item.value as EntitiesStatus)
    }
  />
);

export const InspectionStatusPicker: React.FunctionComponent<PickerProps> = ({ applyValue, item, ...props }) => (
  <InspectionStatusSelect
    {...props}
    onChange={(newValue) => applyValue({ ...item, value: newValue })}
    value={item.value as string | null}
  />
);

export const AuditStatusPicker: React.FunctionComponent<PickerProps> = ({ applyValue, item, ...props }) => (
  <AuditStatusSelect
    {...props}
    onChange={(newValue) => applyValue({ ...item, value: newValue })}
    value={item.value as AuditStatus | null}
  />
);

export const AlarmStatusOperator = (props?: { schema?: Partial<JSONSchemaType> }): GridFilterOperator[] =>
  BaseStatusOperator(AlarmStatusPicker, props, (value) => value[0].toUpperCase() + value.slice(1), true);

export const TaskStatusOperator = (props?: { schema?: Partial<JSONSchemaType> }): GridFilterOperator[] =>
  BaseStatusOperator(TaskStatusPicker, props, (value) => TaskStatusNames[value as TaskStatus]);

export const AmberAlarmStatusOperator = (props?: { schema?: Partial<JSONSchemaType> }): GridFilterOperator[] =>
  BaseStatusOperator(AmberAlarmStatusPicker, props, (value) => AmberAlertStatusName[value as AmberAlertStatus], true);

export const EntitiesStatusOperator = (props?: { schema?: Partial<JSONSchemaType> }): GridFilterOperator[] =>
  BaseStatusOperator(EntitiesStatusPicker, props, (value) => value[0].toUpperCase() + value.slice(1));

export const InspectionStatusOperator = (props?: { schema?: Partial<JSONSchemaType> }): GridFilterOperator[] =>
  BaseStatusOperator(InspectionStatusPicker, props, (value) => value, true);

// "is not" disabled: the backend `auditStatus` filter is inclusion-only.
export const AuditStatusOperator = (props?: { schema?: Partial<JSONSchemaType> }): GridFilterOperator[] =>
  BaseStatusOperator(AuditStatusPicker, props, (value) => AuditStatusNames[value as AuditStatus], true);

export const ScheduleStatusPicker: React.FunctionComponent<PickerProps> = ({ applyValue, item, ...props }) => (
  <ScheduleStatusSelect
    {...props}
    onChange={(newValue) => applyValue({ ...item, value: newValue })}
    value={item.value as ScheduleListStatus | null}
  />
);

const SCHEDULE_STATUS_LABELS: Record<ScheduleListStatus, string> = {
  active: 'Active',
  paused: 'Paused',
};

export const ScheduleStatusOperator = (props?: { schema?: Partial<JSONSchemaType> }): GridFilterOperator[] =>
  BaseStatusOperator(
    ScheduleStatusPicker,
    props,
    (value) => SCHEDULE_STATUS_LABELS[value as ScheduleListStatus] ?? value,
    true
  );

export const AcknowledgementStatusPicker: React.FunctionComponent<PickerProps> = ({ applyValue, item, ...props }) => (
  <AcknowledgementStatusSelect
    {...props}
    onChange={(newValue) => applyValue({ ...item, value: newValue })}
    value={item.value as AcknowledgementRequestStatus | null}
  />
);

export const AcknowledgementStatusOperator = (props?: { schema?: Partial<JSONSchemaType> }): GridFilterOperator[] =>
  BaseStatusOperator(
    AcknowledgementStatusPicker,
    props,
    (value) => ACK_REQUEST_STATUS_LABELS[value as AcknowledgementRequestStatus] ?? value,
    true
  );

export const AcknowledgementStatePicker: React.FunctionComponent<PickerProps> = ({ applyValue, item, ...props }) => (
  <AcknowledgementStateSelect
    {...props}
    onChange={(newValue) => applyValue({ ...item, value: newValue })}
    value={item.value as AcknowledgementState | null}
  />
);

export const AcknowledgementStateOperator = (props?: { schema?: Partial<JSONSchemaType> }): GridFilterOperator[] =>
  BaseStatusOperator(
    AcknowledgementStatePicker,
    props,
    (value) => ACK_STATE_LABELS[value as AcknowledgementState] ?? value,
    true
  );

export const GroupStatusPicker: React.FunctionComponent<PickerProps> = ({ applyValue, item, ...props }) => (
  <GroupStatusSelect
    {...props}
    onChange={(newValue) => applyValue({ ...item, value: newValue })}
    value={item.value as GroupStatusValue | null}
  />
);

const GROUP_STATUS_LABELS: Record<GroupStatusValue, string> = {
  active: 'Active',
  inactive: 'Inactive',
};

export const GroupStatusOperator = (props?: { schema?: Partial<JSONSchemaType> }): GridFilterOperator[] =>
  BaseStatusOperator(
    GroupStatusPicker,
    props,
    (value) => GROUP_STATUS_LABELS[value as GroupStatusValue] ?? value,
    true
  );

export const GroupTypePicker: React.FunctionComponent<PickerProps> = ({ applyValue, item, ...props }) => (
  <GroupTypeSelect
    {...props}
    onChange={(newValue) => applyValue({ ...item, value: newValue })}
    value={item.value as MembershipMode | null}
  />
);

const GROUP_TYPE_LABELS: Record<MembershipMode, string> = {
  [MembershipMode.Manual]: 'Manual',
  [MembershipMode.Dynamic]: 'Dynamic',
};

export const GroupTypeOperator = (props?: { schema?: Partial<JSONSchemaType> }): GridFilterOperator[] =>
  BaseStatusOperator(GroupTypePicker, props, (value) => GROUP_TYPE_LABELS[value as MembershipMode] ?? value, true);
